/**
 * One place for this feature's class strings, expressed in the site's tokens.
 *
 * The first version of these components hardcoded about forty hex values from a
 * palette that exists nowhere else in the codebase, and used font weights that
 * have no font file on disk (DM Sans ships 200 to 600 here, so 700 and 800 were
 * being synthesised into faux bold). Centralising the vocabulary is what stops
 * that happening again: if a colour or a weight is not in this file, it does not
 * belong in the feature.
 *
 * Rules encoded below:
 *   - Weights are `font-medium` (500) and `font-semibold` (600) only. The site
 *     sets even its 66px hero headings in font-medium.
 *   - Ink is `nws-ink`, body copy is `nws-grey-text`, hairlines are
 *     `nws-grey-medium`. Surfaces are `nws-paper` and `nws-grey-light`.
 *   - Status colours come from Tailwind's semantic scale, which is what the rest
 *     of the site already uses (text-red-500, text-emerald-700, text-amber-*).
 *   - Buttons are pills. The site uses rounded-full 131 times; it is the single
 *     most recognisable element in the design language.
 *   - Cards are `rounded-2xl` with `shadow-card`, the site's resting elevation.
 *   - Every text colour here clears 4.5:1 on its intended background.
 */

// ---------------------------------------------------------------------------
// Type scale
// ---------------------------------------------------------------------------

/**
 * Tool-page hero, matching /tools/geo-checker exactly: centred, clamped so it
 * scales without breakpoints, light weight at large size.
 */
export const h1 =
  "w-full text-center font-sans text-[clamp(2rem,5vw,3.75rem)] font-medium leading-[1.05] tracking-[-0.04em] text-nws-ink";

/**
 * Hero subtitle. The 560px cap is the important part: without it the line runs
 * the full column and a long intro becomes a seven-line wall of grey under the
 * headline, which is exactly what went wrong here the first time. If the copy
 * does not fit inside the cap in two lines, the copy is too long.
 */
export const leadHero =
  "mx-auto w-full max-w-[560px] text-center font-sans text-[clamp(1rem,2.5vw,1.25rem)] font-normal leading-[1.5] tracking-[-0.01em] text-nws-ink/60";

/** Major section heading. */
export const h2 =
  "font-sans text-[28px] font-medium leading-[1.1] tracking-[-0.03em] text-nws-ink sm:text-[34px]";

/** Sub-section and card heading. */
export const h3 = "font-sans text-[17px] font-semibold leading-snug text-nws-ink";

/** Small heading inside a panel. */
export const h4 = "font-sans text-[14px] font-semibold text-nws-ink";

/** Lead paragraph under an H1. */
export const lead = "text-[17px] leading-relaxed text-nws-grey-text sm:text-[18px]";

/** Standard body copy. */
export const body = "text-[15px] leading-relaxed text-nws-grey-text";

/** Supporting copy and field help. gray-600 clears AA at 13px; gray-500 does not. */
export const bodySm = "text-[13px] leading-relaxed text-gray-600";

/** Eyebrow / group label. */
export const eyebrow =
  "text-[12px] font-semibold uppercase tracking-wider text-gray-600";

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export const card =
  "rounded-2xl border border-nws-grey-medium bg-white shadow-card";
export const cardFlat = "rounded-2xl border border-nws-grey-medium bg-white";
export const cardPaper = "rounded-2xl border border-nws-grey-medium bg-nws-paper";
export const cardInset = "rounded-xl border border-nws-grey-medium bg-nws-grey-light";

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

export const input =
  "w-full rounded-xl border border-nws-grey-medium bg-white px-3.5 py-2.5 font-sans text-[15px] text-nws-ink " +
  "placeholder:text-gray-600 focus:border-nws-blue focus:outline-none focus:ring-2 focus:ring-nws-blue-light";

export const label = "block font-sans text-[13px] font-semibold text-nws-ink";

/** Primary action. Pill, black, medium weight. */
export const btnPrimary =
  "inline-flex items-center justify-center gap-2 rounded-full bg-black px-6 py-3 font-sans text-[15px] font-medium " +
  "text-white transition-colors hover:bg-nws-ink disabled:cursor-not-allowed disabled:opacity-45";

/** Secondary action. Pill, white, hairline border. */
export const btnSecondary =
  "inline-flex items-center justify-center gap-2 rounded-full border border-nws-grey-medium bg-white px-5 py-2.5 " +
  "font-sans text-[14px] font-medium text-black transition-colors hover:bg-nws-grey-100";

/** Compact secondary, for toolbars. */
export const btnSecondarySm =
  "inline-flex items-center gap-1.5 rounded-full border border-nws-grey-medium bg-white px-3.5 py-1.5 " +
  "font-sans text-[13px] font-medium text-black transition-colors hover:bg-nws-grey-100";

/**
 * Text-only action.
 *
 * Measured on white: `nws-blue-dark` is 5.17:1 and clears AA, the DEFAULT
 * `nws-blue` is 3.68:1 and does not. So blue never appears on a light surface as
 * text, in any state. Hover darkens to ink rather than brightening to blue,
 * which is what the first version did and what made hover the one moment the
 * link failed contrast.
 */
export const btnText =
  "inline-flex items-center gap-1.5 font-sans text-[14px] font-medium text-nws-blue-dark transition-colors hover:text-nws-ink";

/** Inline link inside body copy. Same contrast rule as btnText. */
export const link =
  "font-medium text-nws-blue-dark underline decoration-from-font underline-offset-2 transition-colors hover:text-nws-ink";

// ---------------------------------------------------------------------------
// Status
//
// Tailwind's semantic scale, matching the rest of the site. Each pair is
// contrast-checked for small text on its own tint.
// ---------------------------------------------------------------------------

export const status = {
  error: {
    text: "text-red-700",
    icon: "text-red-600",
    tint: "border-red-200 bg-red-50",
    dot: "bg-red-100",
  },
  warning: {
    text: "text-amber-700",
    icon: "text-amber-600",
    tint: "border-amber-200 bg-amber-50",
    dot: "bg-amber-100",
  },
  success: {
    text: "text-emerald-700",
    icon: "text-emerald-600",
    tint: "border-emerald-200 bg-emerald-50",
    dot: "bg-emerald-100",
  },
  info: {
    text: "text-blue-700",
    icon: "text-blue-600",
    tint: "border-blue-200 bg-blue-50",
    dot: "bg-blue-100",
  },
  neutral: {
    text: "text-gray-600",
    icon: "text-gray-500",
    tint: "border-nws-grey-medium bg-nws-grey-light",
    dot: "bg-nws-grey-200",
  },
} as const;

/** Small pill badge. */
export const badge =
  "rounded-full px-2 py-0.5 font-sans text-[11px] font-semibold";

/** The brand accent panel, for the answer-engine fields. */
export const accentPanel = "rounded-2xl border border-nws-grey-medium bg-nws-yellow/20 p-5";
